import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../../utils/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useOptimisticUpdate } from '../hooks/useOptimisticUpdate';
import { useAuth } from '../../../hooks/useAuth';
import SettingsSection from './SettingsSection';
import SettingItem from './SettingItem';
import ToggleSwitch from './ToggleSwitch';
import { sendSecurityVerificationEmail, verifyEmailCode, clearVerificationCode } from '../../../utils/emailVerificationService';
import profileDeleteService from '../../../utils/profileDeleteService';
import loginSessionService from '../../../utils/loginSessionService';

const VASecurityTab = ({ profileData, onInputChange }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [loginHistory, setLoginHistory] = useState([]);
  const [showLoginHistory, setShowLoginHistory] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [deleteStep, setDeleteStep] = useState(1); // 1: confirmation, 2: type confirmation text

  // Get security settings from main profile data or use defaults
  const securitySettings = profileData.security_settings || {
    suspicious_activity_alerts: true,
    require_verification_for_profile_changes: false
  };

  // Password change state
  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: '',
    security_answer: ''
  });

  // Security questions state - get from dedicated security_questions field
  const [securityQuestions, setSecurityQuestions] = useState(
    profileData?.security_questions?.length > 0 
      ? profileData.security_questions 
      : [
          { id: 1, question: '', answer: '' },
          { id: 2, question: '', answer: '' },
          { id: 3, question: '', answer: '' }
        ]
  );
  
  const [editingSecurityQuestions, setEditingSecurityQuestions] = useState(false);
  const [securityAnswerVerified, setSecurityAnswerVerified] = useState(false);

  // Email verification state for security questions editing
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [enteredVerificationCode, setEnteredVerificationCode] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [verificationCodeSent, setVerificationCodeSent] = useState(false);

  // Predefined security questions
  const PREDEFINED_QUESTIONS = [
    'What is your mother\'s maiden name?',
    'What was the name of your first pet?',
    'What city were you born in?',
    'What is your favorite book?',
    'What was the make of your first car?',
    'What is the name of the street you grew up on?',
    'What is your favorite movie?',
    'What was your first job?',
    'What is your favorite color?',
    'What is the name of your best friend from childhood?'
  ];

  // Shuffle questions for security - memoized to shuffle once per component mount
  const shuffledQuestions = useMemo(() => {
    const shuffled = [...PREDEFINED_QUESTIONS];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, []);

  // Randomly select which configured question to show for verification
  const [verificationQuestion, setVerificationQuestion] = useState(null);



  // Sync security questions from profileData when it changes
  useEffect(() => {
    if (profileData?.security_questions && profileData.security_questions.length > 0) {
      setSecurityQuestions(profileData.security_questions);
      
      // Randomly select a configured question for verification
      const configuredQuestions = profileData.security_questions.filter(q => q.question && q.answer);
      if (configuredQuestions.length > 0) {
        const randomIndex = Math.floor(Math.random() * configuredQuestions.length);
        setVerificationQuestion(configuredQuestions[randomIndex]);
      }
    }
  }, [profileData?.security_questions]);

  // Handle security settings update
  const handleSecuritySettingChange = (setting, value) => {
    const newSettings = {
      ...securitySettings,
      [setting]: value
    };
    
    // Update main profile data
    onInputChange('security_settings', newSettings);
  };
  
  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});
    setSuccessMessage('');
    
    // Check if security questions are set and verify answer
    const hasSecurityQuestions = securityQuestions.some(q => q.question && q.answer);
    if (hasSecurityQuestions && !securityAnswerVerified) {
      setErrors({ security_answer: 'Please verify your security question answer first' });
      return;
    }
    
    // Validate password
    if (passwordData.new_password !== passwordData.confirm_password) {
      setErrors({ confirm_password: 'Passwords do not match' });
      return;
    }
    
    if (passwordData.new_password.length < 8) {
      setErrors({ new_password: 'Password must be at least 8 characters long' });
      return;
    }

    // Check password strength
    const hasUpperCase = /[A-Z]/.test(passwordData.new_password);
    const hasLowerCase = /[a-z]/.test(passwordData.new_password);
    const hasNumbers = /\d/.test(passwordData.new_password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(passwordData.new_password);
    
    if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
      setErrors({ 
        new_password: 'Password must contain uppercase, lowercase, numbers, and special characters' 
      });
      return;
    }
    
    setLoading(true);
    
    try {
      // Create a timeout promise that rejects after 30 seconds (increased from 15)
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Password update request timed out. Please try again.')), 30000)
      );

      // Get the update promise
      const updatePromise = supabase.auth.updateUser({
        password: passwordData.new_password
      });

      // Race between the update and timeout
      const result = await Promise.race([updatePromise, timeoutPromise]);
      
      // Check for error in the result
      if (result?.error) {
        throw new Error(result.error.message || 'Failed to update password');
      }
      
      setSuccessMessage('Password updated successfully!');
      setPasswordData({
        new_password: '',
        confirm_password: '',
        security_answer: ''
      });
      setSecurityAnswerVerified(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('[Password Update Error]:', error);
      setErrors({ 
        general: error?.message || 'Failed to update password. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle security question answer verification
  const handleVerifySecurityAnswer = async () => {
    if (!passwordData.security_answer.trim()) {
      setErrors({ security_answer: 'Please enter your security answer' });
      return;
    }

    // Use the randomly selected verification question
    if (!verificationQuestion) {
      setErrors({ security_answer: 'No security questions configured' });
      return;
    }

    const isCorrect = passwordData.security_answer.toLowerCase().trim() === verificationQuestion.answer.toLowerCase().trim();
    
    if (isCorrect) {
      setSecurityAnswerVerified(true);
      setErrors({});
    } else {
      setErrors({ security_answer: 'Incorrect security answer' });
      setSecurityAnswerVerified(false);
    }
  };

  // Handle security question update
  const handleSaveSecurityQuestions = async () => {
    // Validate that at least one question is filled (first one is required)
    if (!securityQuestions[0]?.question || !securityQuestions[0]?.answer) {
      setErrors({ security_questions: 'Please fill in at least the first security question and answer' });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // Get the current user ID from the profile data
      if (!profileData?.id) {
        throw new Error('User profile not found');
      }

      // Directly update the database with the security questions
      const { error } = await supabase
        .from('user_profiles')
        .update({ security_questions: securityQuestions })
        .eq('id', profileData.id);

      if (error) {
        throw new Error(error.message || 'Failed to save security questions');
      }

      // Update parent state through onInputChange
      // This ensures profileData is updated and synced back down
      onInputChange('security_questions', securityQuestions);
      
      // Show success message
      setSuccessMessage('Security questions saved successfully!');
      
      // Close editing UI after a brief delay to let parent state update
      setTimeout(() => {
        setEditingSecurityQuestions(false);
      }, 100);
      
      // Clear the success message after 4 seconds to allow user to see the "Configured" badge
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (error) {
      console.error('[Security Questions Update Error]:', error);
      setErrors({ security_questions: error.message || 'Failed to update security questions' });
    } finally {
      setLoading(false);
    }
  };

  // Handle security question field change
  const handleSecurityQuestionChange = (index, field, value) => {
    const updatedQuestions = [...securityQuestions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      [field]: value
    };
    setSecurityQuestions(updatedQuestions);
  };

  // Handle sending verification email for security questions editing
  const handleSendVerificationEmail = async () => {
    try {
      setLoading(true);
      setErrors({});

      const userEmail = profileData?.email;
      if (!userEmail) {
        throw new Error('Email not found in profile');
      }

      const result = await sendSecurityVerificationEmail(
        userEmail,
        profileData?.full_name,
        'va'
      );

      if (!result.success) {
        throw new Error(result.error);
      }

      setVerificationCodeSent(true);
      setSuccessMessage('Verification code sent to your email');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Error sending verification email:', error);
      setErrors({ email_verification: error.message || 'Failed to send verification email' });
    } finally {
      setLoading(false);
    }
  };

  // Handle email verification code verification
  const handleVerifyEmail = () => {
    try {
      setErrors({});

      if (!enteredVerificationCode || enteredVerificationCode.length !== 6) {
        setErrors({ email_verification: 'Please enter a valid 6-digit code' });
        return;
      }

      const result = verifyEmailCode(profileData?.email, enteredVerificationCode);

      if (!result.success) {
        setErrors({ email_verification: result.error });
        return;
      }

      setEmailVerified(true);
      setSuccessMessage('Email verified! You can now edit your security questions.');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Proceed to edit mode after brief delay
      setTimeout(() => {
        setEditingSecurityQuestions(true);
        setShowEmailVerification(false);
        setEnteredVerificationCode('');
        setVerificationCodeSent(false);
      }, 500);
    } catch (error) {
      console.error('Error verifying email:', error);
      setErrors({ email_verification: 'An error occurred while verifying the code' });
    }
  };

  // Handle canceling email verification
  const handleCancelEmailVerification = () => {
    setShowEmailVerification(false);
    setEnteredVerificationCode('');
    setVerificationCodeSent(false);
    setEmailVerified(false);
    clearVerificationCode(profileData?.email);
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    if (deleteStep === 1) {
      setDeleteStep(2);
      return;
    }

    // Verify confirmation text
    if (deleteConfirmationText !== 'DELETE MY ACCOUNT') {
      setErrors({ delete_account: 'You must type DELETE MY ACCOUNT exactly to confirm' });
      return;
    }

    try {
      setLoading(true);
      setErrors({});

      if (!user?.id) {
        throw new Error('User ID not found');
      }

      const result = await profileDeleteService.deleteUserAccount(user.id);

      if (!result.success) {
        throw new Error(result.error || 'Failed to delete account');
      }

      // Show success message briefly
      setSuccessMessage('Your account is being deleted. Redirecting to homepage...');
      setShowDeleteConfirmation(false);

      // Redirect after brief delay to allow user to see success message
      setTimeout(() => {
        window.location.href = '/?account_deleted=true';
      }, 2000);
    } catch (error) {
      console.error('Error deleting account:', error);
      setErrors({ delete_account: error.message || 'Failed to delete account. Please try again or contact support.' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch real login history data
  const fetchLoginHistory = async () => {
    try {
      setLoading(true);
      setShowLoginHistory(true);
      
      if (!profileData?.id) {
        throw new Error('User profile not found');
      }

      // Fetch login history from service
      const history = await loginSessionService.getLoginHistory(profileData.id, 15);
      
      // Transform data to match component structure
      const transformedHistory = history.map(session => ({
        id: session.id,
        device: session.device_info,
        browser: session.browser_info,
        location: session.location,
        ip_address: session.ip_address,
        timestamp: new Date(session.login_time),
        status: 'success', // All logged sessions are successful
        is_current: session.is_current
      }));

      setLoginHistory(transformedHistory);
      
      // Check for suspicious activity
      const suspiciousActivity = await loginSessionService.checkSuspiciousActivity(profileData.id);
      if (suspiciousActivity.isSuspicious && securitySettings.suspicious_activity_alerts) {
        setSuccessMessage(`⚠️ Suspicious activity detected: ${suspiciousActivity.factors.join(', ')}`);
        setTimeout(() => setSuccessMessage(''), 5000);
      }
      
    } catch (error) {
      console.error('Error fetching login history:', error);
      setErrors({ general: 'Failed to load login history. Please try again.' });
      
      // Fallback to mock data for demonstration
      setLoginHistory([
        {
          id: 'mock-1',
          device: 'Current Device',
          browser: 'Current Browser',
          location: 'Current Location',
          ip_address: '192.168.1.1',
          timestamp: new Date(),
          status: 'success',
          is_current: true
        }
      ]);
    } finally {
      setLoading(false);
    }
  };



  const getStatusIndicator = () => {    
    if (successMessage) {
      return (
        <div className="flex items-center text-sm text-green-600">
          <Icon name="CheckCircle" size={16} className="mr-1" />
          Saved
        </div>
      );
    }
    
    if (errors.settings) {
      return (
        <div className="flex items-center text-sm text-red-600">
          <Icon name="AlertCircle" size={16} className="mr-1" />
          Error saving
        </div>
      );
    }
    
    return null;
  };

  return (
    <div className="space-y-8">
      {/* Status Indicator */}
      {(successMessage || errors.settings) && (
        <div className="flex justify-end">
          {getStatusIndicator()}
        </div>
      )}

      {/* Password Change Section */}
      <form onSubmit={handlePasswordChange} className="bg-surface rounded-lg border border-border p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-text-primary">Change Password</h3>
          <Icon name="Key" size={20} className="text-text-tertiary" />
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              New Password
            </label>
            <Input
              type="password"
              value={passwordData.new_password}
              onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
              error={errors.new_password}
              placeholder="Enter new password"
              required
            />
            <p className="text-xs text-text-tertiary mt-1">
              Password must be at least 8 characters with uppercase, lowercase, numbers, and special characters
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Confirm New Password
            </label>
            <Input
              type="password"
              value={passwordData.confirm_password}
              onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
              error={errors.confirm_password}
              placeholder="Confirm new password"
              required
            />
          </div>
          
          <div className="flex justify-end">
            <Button
              type="submit"
              variant="primary"
              loading={loading}
              disabled={loading || !passwordData.new_password || !passwordData.confirm_password}
            >
              {loading ? 'Updating...' : 'Update Password'}
            </Button>
          </div>
        </div>
      </form>

      {/* Security Questions Section */}
      <div className="bg-surface rounded-lg border border-border p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-text-primary">Security Questions</h3>
            {profileData?.security_questions?.some(q => q.question && q.answer) && (
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                Configured
              </span>
            )}
          </div>
          <Icon name="Shield" size={20} className="text-text-tertiary" />
        </div>

        {editingSecurityQuestions ? (
          <>
            {/* Edit form - shown when editing */}
            <div className="space-y-4">
              {securityQuestions.map((q, index) => (
                <div key={q.id} className="space-y-2 p-4 bg-surface-secondary rounded-lg">
                  <label className="block text-sm font-medium text-text-primary">
                    Question {index + 1} {index === 0 && <span className="text-error">*</span>}
                  </label>
                  <select
                    value={q.question}
                    onChange={(e) => handleSecurityQuestionChange(index, 'question', e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-surface text-text-primary text-sm"
                  >
                    <option value="">Select a security question</option>
                    {shuffledQuestions.map((question, idx) => (
                      <option key={idx} value={question}>
                        {question}
                      </option>
                    ))}
                  </select>

                  <label className="block text-sm font-medium text-text-primary">
                    Your Answer {index === 0 && <span className="text-error">*</span>}
                  </label>
                  <Input
                    type="text"
                    value={q.answer}
                    onChange={(e) => handleSecurityQuestionChange(index, 'answer', e.target.value)}
                    placeholder="Enter your answer (case-insensitive)"
                  />
                </div>
              ))}

              <div className="flex gap-2 justify-end pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setEditingSecurityQuestions(false);
                    // Reset to saved data from profileData
                    const savedQuestions = profileData?.security_questions?.length > 0 
                      ? profileData.security_questions 
                      : [
                          { id: 1, question: '', answer: '' },
                          { id: 2, question: '', answer: '' },
                          { id: 3, question: '', answer: '' }
                        ];
                    setSecurityQuestions(savedQuestions);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleSaveSecurityQuestions}
                  loading={loading}
                >
                  Save Questions
                </Button>
              </div>
            </div>
          </>
        ) : profileData?.security_questions?.some(q => q.question && q.answer) ? (
          <>
            {/* Display verification section if questions are configured */}
            <div className="space-y-4">
              <p className="text-sm text-text-secondary mb-4">
                To update your password, please answer your security question:
              </p>
              
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  {verificationQuestion?.question}
                </label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={passwordData.security_answer}
                    onChange={(e) => {
                      setPasswordData({...passwordData, security_answer: e.target.value});
                      setSecurityAnswerVerified(false);
                    }}
                    error={errors.security_answer}
                    placeholder="Enter your answer"
                    disabled={securityAnswerVerified}
                  />
                  <Button
                    type="button"
                    variant={securityAnswerVerified ? 'success' : 'outline'}
                    onClick={handleVerifySecurityAnswer}
                    disabled={securityAnswerVerified || !passwordData.security_answer}
                  >
                    {securityAnswerVerified ? 'Verified ✓' : 'Verify'}
                  </Button>
                </div>
              </div>

              <div className="flex justify-between items-center pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowEmailVerification(true);
                    handleSendVerificationEmail();
                  }}
                >
                  <Icon name="Edit" size={16} className="mr-1" />
                  Edit Questions
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Display setup section if questions are not configured */}
            <div className="space-y-4">
              <p className="text-sm text-text-secondary mb-4">
                Set up security questions to add an extra layer of protection when changing your password.
              </p>

              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowEmailVerification(true);
                  handleSendVerificationEmail();
                }}
              >
                <Icon name="Plus" size={16} className="mr-2" />
                Set Up Security Questions
              </Button>
            </div>
          </>
        )}

        {errors.security_questions && (
          <div className="mt-4 bg-error-50 border border-error-200 rounded-lg p-3">
            <div className="flex">
              <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0 mr-2 mt-0.5" />
              <p className="text-sm text-error">{errors.security_questions}</p>
            </div>
          </div>
        )}
      </div>

      {/* Account Security Settings */}
      <SettingsSection
        title="Account Security"
        description="Protect your VA account with advanced security features"
        icon="Shield"
      >
        <div className="space-y-4">
          <SettingItem
            title="Suspicious Activity Alerts"
            description="Get alerts for unusual account activity"
            control={
              <ToggleSwitch
                checked={securitySettings.suspicious_activity_alerts}
                onChange={(checked) => handleSecuritySettingChange('suspicious_activity_alerts', checked)}
              />
            }
          />
        </div>
      </SettingsSection>

      {/* Professional Security */}
      <SettingsSection
        title="Professional Security"
        description="Security settings specific to your VA work"
        icon="Briefcase"
      >
        <div className="space-y-4">
          <SettingItem
            title="Profile Change Verification"
            description="Require additional verification for profile updates"
            control={
              <ToggleSwitch
                checked={securitySettings.require_verification_for_profile_changes}
                onChange={(checked) => handleSecuritySettingChange('require_verification_for_profile_changes', checked)}
              />
            }
          />
        </div>
      </SettingsSection>



      {/* Login History Modal/Section */}
      {showLoginHistory && (
        <div className="bg-surface rounded-lg border border-border p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-text-primary">Login History</h3>
            <Button
              type="button"
              variant="text"
              size="sm"
              onClick={() => setShowLoginHistory(false)}
            >
              <Icon name="X" size={16} />
            </Button>
          </div>
          
          <div className="space-y-4">
            {loginHistory.map((entry) => (
              <div key={entry.id} className="bg-surface-secondary rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className={`p-2 rounded-full ${entry.is_current ? 'bg-primary-100' : 'bg-secondary-100'}`}>
                      <Icon 
                        name={entry.device.includes('iPhone') || entry.device.includes('Android') ? 'Smartphone' : 'Monitor'} 
                        size={20} 
                        className={entry.is_current ? 'text-primary' : 'text-text-tertiary'} 
                      />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-text-primary">
                        {entry.device} • {entry.browser}
                      </h4>
                      <p className="text-xs text-text-secondary">
                        {entry.location} • {entry.ip_address}
                      </p>
                      <p className="text-xs text-text-tertiary mt-1">
                        {entry.is_current ? 'Active now' : entry.timestamp.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {entry.is_current ? (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                        Current Session
                      </span>
                    ) : (
                      <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-full">
                        {entry.status}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error and Success Messages */}
      {errors.general && (
        <div className="bg-error-50 border border-error-200 rounded-lg p-4">
          <div className="flex">
            <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mr-2" />
            <p className="text-sm text-error">{errors.general}</p>
          </div>
        </div>
      )}

      {/* Email Verification Modal */}
      {showEmailVerification && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                <Icon name="Lock" size={20} className="text-primary" />
                Verify Your Email
              </h3>
              <button
                onClick={handleCancelEmailVerification}
                className="text-text-secondary hover:text-text-primary transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {!verificationCodeSent ? (
              <div className="space-y-4">
                <p className="text-sm text-text-secondary">
                  A verification code will be sent to your email address. Please verify it to proceed.
                </p>
                <Button
                  onClick={handleSendVerificationEmail}
                  variant="primary"
                  className="w-full"
                  loading={loading}
                >
                  <Icon name="Mail" size={16} className="mr-2" />
                  Send Verification Code
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-text-secondary">
                  Enter the 6-digit verification code sent to <span className="font-medium">{profileData?.email}</span>
                </p>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    maxLength="6"
                    value={enteredVerificationCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                      setEnteredVerificationCode(value.slice(0, 6));
                    }}
                    placeholder="000000"
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-center text-2xl tracking-widest font-mono"
                    disabled={emailVerified}
                  />
                  {emailVerified && (
                    <p className="text-sm text-success mt-2 flex items-center gap-1">
                      <Icon name="CheckCircle" size={16} />
                      Email verified successfully!
                    </p>
                  )}
                </div>

                <p className="text-xs text-text-secondary">
                  Code expires in 10 minutes
                </p>

                {errors.email_verification && (
                  <div className="bg-error-50 border border-error-200 rounded-lg p-3">
                    <div className="flex">
                      <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0 mr-2 mt-0.5" />
                      <p className="text-sm text-error">{errors.email_verification}</p>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={handleCancelEmailVerification}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    className="flex-1"
                    onClick={handleVerifyEmail}
                    loading={loading}
                    disabled={emailVerified}
                  >
                    Verify
                  </Button>
                </div>

                <button
                  type="button"
                  onClick={handleSendVerificationEmail}
                  className="w-full text-sm text-primary hover:text-primary-dark transition-colors py-2"
                >
                  Send code again
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Account Section */}
      <SettingsSection
        title="Delete Account"
        description="Permanently delete your VA account and all associated data"
        icon="Trash"
      >
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">
            Once you delete your account, there is no going back. Please be certain.
          </p>
          <div className="bg-error-50 border border-error-200 rounded-lg p-4">
            <div className="flex gap-3">
              <Icon name="AlertTriangle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-error mb-2">What will be deleted:</h4>
                <ul className="text-sm text-error space-y-1 ml-4 list-disc">
                  <li>Your profile and all personal information</li>
                  <li>Your job applications and history</li>
                  <li>Your messages and conversations</li>
                  <li>Your work history and time entries</li>
                  <li>Your profile picture and portfolio</li>
                  <li>All associated account data</li>
                </ul>
              </div>
            </div>
          </div>
          <Button
            type="button"
            variant="danger"
            onClick={() => {
              setShowDeleteConfirmation(true);
              setDeleteStep(1);
              setDeleteConfirmationText('');
            }}
            disabled={loading}
          >
            <Icon name="Trash" size={16} className="mr-2" />
            Delete My Account
          </Button>
        </div>
      </SettingsSection>

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-error flex items-center gap-2">
                <Icon name="AlertTriangle" size={20} />
                Delete Account
              </h3>
              <button
                onClick={() => {
                  setShowDeleteConfirmation(false);
                  setDeleteStep(1);
                  setDeleteConfirmationText('');
                  setErrors({});
                }}
                className="text-text-secondary hover:text-text-primary transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {deleteStep === 1 ? (
              <div className="space-y-4">
                <div className="bg-error-50 border border-error-200 rounded-lg p-3">
                  <p className="text-sm text-error font-semibold mb-2">⚠️ This action cannot be undone!</p>
                  <p className="text-sm text-error">
                    Deleting your account will permanently remove all your data, including profile, messages, applications, and work history.
                  </p>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setShowDeleteConfirmation(false);
                      setDeleteStep(1);
                      setDeleteConfirmationText('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    className="flex-1"
                    onClick={handleDeleteAccount}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-text-secondary">
                  Type <span className="font-mono font-semibold text-error">DELETE MY ACCOUNT</span> to confirm deletion:
                </p>
                <div>
                  <Input
                    type="text"
                    value={deleteConfirmationText}
                    onChange={(e) => {
                      setDeleteConfirmationText(e.target.value);
                      setErrors({});
                    }}
                    placeholder="Type confirmation text here"
                    autoFocus
                  />
                  {errors.delete_account && (
                    <p className="text-sm text-error mt-2">{errors.delete_account}</p>
                  )}
                </div>
                <div className="flex gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setDeleteStep(1);
                      setDeleteConfirmationText('');
                      setErrors({});
                    }}
                    disabled={loading}
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    className="flex-1"
                    onClick={handleDeleteAccount}
                    loading={loading}
                    disabled={loading || deleteConfirmationText !== 'DELETE MY ACCOUNT'}
                  >
                    Delete Account
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {successMessage && (
        <div className="bg-success-50 border border-success-200 rounded-lg p-4">
          <div className="flex">
            <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mr-2" />
            <p className="text-sm text-success">{successMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default VASecurityTab;